import {
  batch,
  createContext,
  createEffect,
  createMemo,
  createResource,
  JSX,
  on,
  untrack,
  useContext,
} from 'solid-js';
import { useAuth } from '@trimble/trimble-id-solidjs';
import { useLogger } from '@trimble/solidjs-utils';
import { useConnectProject } from '@trimble/connect-project-providers';
import {
  DrawingTool,
  FeatureScope,
  GeospatialFeature,
  MapLayer,
} from '@trimble/geospatial';
import { useMobileMappingFlags } from '../feature-flags/mobile-mapping-feature-flags';

import { EXPERIMENTS_KEYS, useAppContext } from '../AppContextProvider';
import { useDataContext } from '../data-state/DataContextProvider';
import { getVisiblePointCloudIds } from '../data-state/DataContextProviderHelpers';
import { isFeatureExtractionEditingEligible } from './feature-extraction-eligibility';
import type {
  FeatureExtractionContextType,
  FeatureExtractionLayersLoadStatus,
} from './feature-extraction-context.types';
import {
  createDefaultFeatureExtractionState,
  createFeatureExtractionStateModel,
  FeatureExtractionState,
} from './feature-extraction-state-model';
import { createStore, reconcile } from 'solid-js/store';
import { createFeatureExtractionLoaders } from './feature-extraction-loaders';
import {
  createFeatureExtractionRepositories,
  createMapsRegion,
  resolveFxTilesServiceBaseUrl,
} from './feature-extraction-repositories';
import { createTileCache } from './feature-extraction-tile-cache';
import { createCadEditingUiState } from './create-cad-editing-ui-state';

export type {
  FeatureExtractionContextType,
  FeatureExtractionLayersLoadStatus,
} from './feature-extraction-context.types';

const FeatureExtractionContext = createContext<FeatureExtractionContextType>();

export interface FeatureExtractionContextProviderProps {
  children: JSX.Element;
  fxMapsServiceBaseUrl?: string;
  fxFeaturesServiceBaseUrl?: string;
  fxTilesServiceBaseUrl?: string;
}

export function FeatureExtractionContextProvider(
  props: Readonly<FeatureExtractionContextProviderProps>
): JSX.Element {
  const auth = useAuth();
  const loggerContext = useLogger();
  const logger = loggerContext.getLogger('FeatureExtraction');
  const appContext = useAppContext();
  const { hasFlag } = useMobileMappingFlags();
  const { selectedProjectId, selectedRegion } = useConnectProject();
  const dataContext = useDataContext();

  const mapsRegion = createMapsRegion(() => selectedRegion()?.location);
  const getAccessToken = (): Promise<string> => auth().getAccessTokenSilently();

  const [editingState, setEditingState] = createStore<FeatureExtractionState>(
    createDefaultFeatureExtractionState()
  );
  const { tileCacheToken, bumpTileCacheToken } = createTileCache();
  const {
    upsertFeatures,
    upsertFeature,
    mergeFeatureProperties,
    removeFeature,
    getSceneAnchors,
    setSceneAnchors,
    registerFeatureRetention,
    evictDistantFeatures,
    isLayerVisible,
    toggleLayerVisibility,
    setVisibleLayerIds,
    setLayerStyle,
    applyProjectLayers,
  } = createFeatureExtractionStateModel({
    editingState,
    setEditingState,
    bumpTileCacheToken,
  });

  // createResource so Solid owns lifecycle; .latest for sync readers (tile fetcher).
  const [accessToken] = createResource(auth, async (currentAuth) => {
    try {
      return await currentAuth.getAccessTokenSilently();
    } catch (error) {
      logger.error(
        'Failed to acquire access token',
        undefined,
        error instanceof Error ? error : new Error(String(error))
      );
      throw error;
    }
  });
  const authToken = (): string | undefined => accessToken.latest;

  const isFeatureExtractionEnabled = createMemo(() =>
    hasFlag('FeatureExtraction')
  );

  const isFeaturesDebugEnabled = createMemo(
    () => appContext.experiments()[EXPERIMENTS_KEYS['featuresDebug']] === true
  );

  const georeferencedVisiblePointCloudIds = createMemo(() => {
    const csibs = dataContext.loadedCsibs();
    return (getVisiblePointCloudIds(dataContext.dataState) ?? []).filter((id) =>
      csibs.has(id)
    );
  });

  const visiblePointCloudCount = createMemo(
    () => georeferencedVisiblePointCloudIds().length
  );

  // Mercator authors only in a point cloud's CRS: the project CRS tier has no
  // CAD session, and a cloud whose URN could not be derived has nothing to send.
  const hasAuthoringCrs = createMemo(() => {
    const crs = dataContext.activeCrs();
    return crs?.pointCloudId !== undefined && crs.crsUrn !== undefined;
  });

  const isEditingEligibleAccessor = createMemo(() =>
    isFeatureExtractionEditingEligible({
      isFeatureFlagEnabled: isFeatureExtractionEnabled(),
      projectId: selectedProjectId(),
      visiblePointCloudCount: visiblePointCloudCount(),
      hasAuthoringCrs: hasAuthoringCrs(),
    })
  );

  createEffect(() => {
    if (!isFeatureExtractionEnabled() || visiblePointCloudCount() === 0) {
      return;
    }
    const crs = dataContext.activeCrs();
    if (crs?.pointCloudId !== undefined && crs.crsUrn === undefined) {
      logger.warn('Active point cloud has no CRS URN; editing is disabled', {
        pointCloudId: crs.pointCloudId,
      });
    }
  });

  // Detects, does not guard: a mission does not change CRS (MERCATOR-319).
  createEffect(() => {
    const active = dataContext.activeCrs();
    if (!isFeatureExtractionEnabled() || active?.pointCloudId === undefined) {
      return;
    }
    const csibs = dataContext.loadedCsibs();
    for (const id of georeferencedVisiblePointCloudIds()) {
      const csib = csibs.get(id);
      if (id !== active.pointCloudId && csib !== active.csib64) {
        logger.warn('Visible point clouds use different CRSs', {
          activePointCloudId: active.pointCloudId,
          activeCsib: active.csib64,
          pointCloudId: id,
          csib,
        });
      }
    }
  });

  const scope = createMemo<FeatureScope | undefined>(() => {
    const projectId = selectedProjectId();
    return projectId ? { projectId } : undefined;
  });

  const selectedLayer = createMemo<MapLayer | undefined>(() => {
    const id = editingState.selectedLayerId;
    return id
      ? editingState.layers.find((layer) => layer.id === id)
      : undefined;
  });

  // Every tool works on every layer: the user picks the layer, not the tool.
  const allowedDrawingTools = (): readonly DrawingTool[] => [
    DrawingTool.Point,
    DrawingTool.CompoundCurve,
    DrawingTool.Rectangle,
    DrawingTool.Polygon,
  ];

  // Accessor is consumed later from event handlers and memos, not at setup.
  // eslint-disable-next-line solid/reactivity
  const cadUiState = createCadEditingUiState(
    allowedDrawingTools,
    isFeatureExtractionEnabled
  );

  const setDesiredDrawingTool = (tool: DrawingTool | undefined): void => {
    if (tool !== undefined && selectedLayer() === undefined) {
      return;
    }
    cadUiState.setDesiredDrawingTool(tool);
  };

  const onSelectedLayerChanged = (): void => {
    if (selectedLayer() === undefined) {
      cadUiState.setDesiredDrawingTool(undefined);
      return;
    }
    cadUiState.onSelectedLayerChanged();
  };

  const setSelectedLayerId = (id: string | undefined): void => {
    if (editingState.selectedLayerId === id) {
      return;
    }
    batch(() => {
      setEditingState('selectedLayerId', id);
      onSelectedLayerChanged();
    });
  };

  const featureIds = createMemo((): readonly string[] =>
    Object.keys(editingState.features)
  );

  const getFeature = (id: string): GeospatialFeature | undefined =>
    editingState.features[id];

  const repositories = createFeatureExtractionRepositories({
    envName: () => appContext.envName,
    region: mapsRegion,
    logger,
    getAccessToken,
    authToken,
    fxMapsServiceBaseUrl: () => props.fxMapsServiceBaseUrl,
    fxFeaturesServiceBaseUrl: () => props.fxFeaturesServiceBaseUrl,
  });

  const tilesBaseUrl = createMemo(() => {
    const region = mapsRegion();
    if (region === undefined) {
      return '';
    }
    return resolveFxTilesServiceBaseUrl(
      props.fxTilesServiceBaseUrl,
      region,
      appContext.envName
    );
  });

  const { layersFetchKey, layersQuery } = createFeatureExtractionLoaders({
    isFeatureExtractionEnabled,
    selectedProjectId,
    authToken,
    mapsRegion,
    repositories,
    logger,
  });

  const clearContext = (): void => {
    batch(() => {
      setEditingState(reconcile(createDefaultFeatureExtractionState()));
      bumpTileCacheToken();
      cadUiState.reset();
    });
  };

  const resetEditingState = (): void => {
    if (editingState.selectedFeature === undefined) {
      return;
    }
    setEditingState('selectedFeature', undefined);
  };

  const layersLoadStatus = createMemo((): FeatureExtractionLayersLoadStatus => {
    if (layersFetchKey() === undefined) {
      return 'idle';
    }
    if (layersQuery.isFetching) {
      return 'pending';
    }
    if (layersQuery.isError) {
      return editingState.initialized ? 'success' : 'error';
    }
    return editingState.initialized ? 'success' : 'pending';
  });

  createEffect(
    on([selectedProjectId, mapsRegion], ([projectId, region], previous) => {
      const [previousProjectId, previousRegion] = previous ?? [];
      if (projectId !== previousProjectId || region !== previousRegion) {
        clearContext();
      }
    })
  );

  createEffect(() => {
    if (layersFetchKey() === undefined) {
      if (untrack(() => editingState.initialized)) {
        clearContext();
      }
      return;
    }

    if (!layersQuery.isSuccess) {
      return;
    }

    const data = layersQuery.data;
    // eslint-disable-next-line sonarjs/different-types-comparison -- query data can be undefined at runtime despite the success narrowing
    if (data === undefined) {
      return;
    }
    untrack(() => {
      batch(() => {
        applyProjectLayers(data);
        onSelectedLayerChanged();
      });
    });
  });

  const value: FeatureExtractionContextType = {
    isFeatureExtractionEnabled,
    isEditingEligible: isEditingEligibleAccessor,
    layersLoadStatus,
    layerRepository: () => repositories()?.layerRepository,
    featureRepository: () => repositories()?.featureRepository,
    featuresApiService: () => repositories()?.featuresApiService,
    isFeaturesDebugEnabled,
    scope,
    editingState,
    isLayerVisible,
    toggleLayerVisibility,
    setVisibleLayerIds,
    setLayerStyle,
    setSelectedLayerId,
    selectedLayer,
    setSelectedFeature: (feature: GeospatialFeature | undefined) => {
      if (editingState.selectedFeature === feature) {
        return;
      }
      setEditingState('selectedFeature', feature);
    },
    getFeature,
    featureIds,
    getSceneAnchors,
    setSceneAnchors,
    registerFeatureRetention,
    evictDistantFeatures,
    upsertFeatures,
    upsertFeature,
    mergeFeatureProperties,
    removeFeature,
    activeCrs: dataContext.activeCrs,
    authToken,
    tileCacheToken,
    bumpTileCacheToken,
    tilesBaseUrl,
    reset: resetEditingState,
    cadEditingMode: cadUiState.cadEditingMode,
    desiredDrawingTool: cadUiState.desiredDrawingTool,
    allowedDrawingTools: cadUiState.allowedDrawingTools,
    setDesiredDrawingTool,
  };

  return (
    <FeatureExtractionContext.Provider value={value}>
      {props.children}
    </FeatureExtractionContext.Provider>
  );
}

export function useFeatureExtractionContext(): FeatureExtractionContextType {
  const context = useContext(FeatureExtractionContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureExtractionContext must be used within a FeatureExtractionContextProvider'
    );
  }
  return context;
}
